
import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import AddDSPModal from "../../components/modal/AddDSPModal";
import EditDSPModal from "../../components/modal/EditDSPModal";
import { dspApi } from "../../repositories/dsp";
import { AddDSPData, DSPData, EditDSPData } from "@/components/types/types";
import { Loader } from "lucide-react";
import { formatDSPName } from "../../utils/formatDSP";
const DSP: React.FC = () => {
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [dspList, setDspList] = useState<DSPData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentDsp, setCurrentDsp] = useState<DSPData | null>(null);

  const fetchDSPs = async () => {
    try {
      setLoading(true);
      const response = await dspApi.getDSPs();
      setDspList(response.data.data);
    } catch (error: any) {
      if (error.response?.status === 404) {
        setDspList([]);
      } else {
        Swal.fire(
          "Error",
          error?.response?.data?.message ||
            error.message ||
            "An unknown error occurred",
          "error"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDSPs();
  }, []);

  const handleAddDSP = async (dspData: AddDSPData): Promise<void> => {
    try {
      await dspApi.addDSP(dspData);
      Swal.fire("Success", "DSP added successfully", "success");
      setIsAddModalOpen(false);
      fetchDSPs();
    } catch (error: any) {
      Swal.fire(
        "Error",
        error?.response?.data?.message ||
          error.message ||
          "An unknown error occurred",
        "error"
      );
    }
  };

  const handleEditDsp = async (dspData: EditDSPData): Promise<void> => {
    try {
      if (currentDsp) {
        await dspApi.updateDSP(currentDsp.id, dspData);
        Swal.fire("Success", "DSP updated successfully", "success");
        setIsEditModalOpen(false);
        setCurrentDsp(null);
        fetchDSPs();
      }
    } catch (error: any) {
      Swal.fire(
        "Error",
        error?.response?.data?.message ||
          error.message ||
          "An unknown error occurred",
        "error"
      );
    }
  };
// AddDSP button handler
const handleAddButtonClick = () => {
  if (isEditModalOpen) setIsEditModalOpen(false); 
  setCurrentDsp(null);
  setIsAddModalOpen(true);
};
  const handleEditClick = (dsp: DSPData) => {
    setCurrentDsp(dsp);
    setIsEditModalOpen(true);
    setIsAddModalOpen(false);
  };
  const handleDeleteDsp = async (dspId: string, dspType: string) => {
    try {
      const dspIdNumber = Number(dspId);
      if (isNaN(dspIdNumber)) throw new Error("Invalid DSP ID");
  
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      });
  
      if (result.isConfirmed) {
        await dspApi.deleteDSP(dspIdNumber, dspType);
        setDspList((prev) =>
          prev.filter((dsp) => Number(dsp.id) !== dspIdNumber)
        );
        Swal.fire("Deleted!", "Your DSP has been deleted.", "success");
      }
    } catch (error: any) {
      Swal.fire(
        "Error",
        error?.response?.data?.message ||
          error.message ||
          "An unknown error occurred",
        "error"
      );
    }
  };
  

  return (
    <div className="min-h-screen p-6 rounded-lg shadow-lg bg-gray-50">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-700">DSP Management</h1>
        {/* <button
          onClick={handleAddButtonClick} 
          className="px-5 py-2 text-white transition bg-blue-600 rounded-lg hover:bg-blue-700"
        >
          Add DSP
        </button> */}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader size={40} className="text-gray-600 animate-spin" />
        </div>
      ) : dspList.length === 0 ? (
        <div className="flex items-center justify-center h-64 text-gray-500">
          No DSP accounts found.
        </div>
      ) : (
        <table className="w-full bg-white border rounded-lg shadow-md">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-3 text-left border">ID</th>
              <th className="p-3 text-left border">Name</th>
              <th className="p-3 text-left border">Email</th>
              <th className="p-3 text-left border">Type</th>
              <th className="p-3 text-left border">Actions</th>
            </tr>
          </thead>
          <tbody>
            {dspList.map((dsp, index) => (
              <tr
                key={dsp.id}
                className={index % 2 === 0 ? "bg-gray-50" : "bg-white"}
              >
                <td className="p-3 border">{dsp.id}</td>
                <td className="p-3 border">{dsp.name}</td>
                <td className="p-3 border">{dsp.email}</td>
                <td className="p-3 border">{formatDSPName(dsp.type)}</td>
                <td className="flex gap-2 p-3 border">
                  <button
                    onClick={() => handleEditClick(dsp)}
                    className="px-3 py-1 text-white bg-yellow-500 rounded hover:bg-yellow-600"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteDsp(dsp.id, dsp.type)}
                    className="px-3 py-1 text-white bg-red-500 rounded hover:bg-red-600"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <AddDSPModal
        // key={isAddModalOpen ? "add-open" : "add-closed"}
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleAddDSP}
        // userId={userId}
        dspData={currentDsp}
      />
      <EditDSPModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSubmit={handleEditDsp}
        dspData={
          currentDsp || { id: "", type: "", name: "", email: "",password:"", createdAt: "" }
        }
      />
    </div>
  );
};

export default DSP;
